/**
 * 航段选择表格组件
 * 包含完整筛选功能的航段选择表格
 *
 * 跨页勾选策略：
 *   - 以 selectedRowKeys 为唯一真相
 *   - 用 legsCache 把 key 还原成带 BH 的完整航段对象
 *   - 绝不依赖 Ant Design onChange 的 selectedRows（它只有当前页，翻页会丢）
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Table, Button, Space, Checkbox, Input, DatePicker, Row, Col, Form, message, Spin } from "antd";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { settlementApi } from "../../../../api/settlementApi";

const { RangePicker } = DatePicker;

const hasValidBH = (row) => row.blockHours != null && row.blockHours > 0;

/** 单次拉取筛选结果的上限（与后端 acmi-records le=500 对齐） */
const MAX_FETCH_PAGE_SIZE = 500;

/**
 * 航段选择表格组件
 */
const TargetLegTable = ({
    selectedRowKeys = [],
    selectedLegs = [],
    onSelectionChange,
    showBHColumn = true,
    validateBH = false,
    showValidationStatus = false,
    // BH×单价：权威 BH 走 acmi-records；平均分摊走 allocation-target-legs
    useAuthoritativeBH = false,
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [selectingAll, setSelectingAll] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [totalRecords, setTotalRecords] = useState(0);
    const [legData, setLegData] = useState([]);

    const [filters, setFilters] = useState({
        flightNumber: "",
        departureAirport: "",
        arrivalAirport: "",
        dateRange: null,
        aircraftNumber: "",
    });

    // 访问过的航段缓存：跨页还原 BH / 航班字段
    const legsCacheRef = useRef(new Map());

    const rememberLegs = useCallback((items) => {
        (items || []).forEach((item) => {
            if (item?.id != null) {
                legsCacheRef.current.set(item.id, item);
            }
        });
    }, []);

    // 父组件已选对象也写入缓存（防止只剩 key、丢 BH）
    useEffect(() => {
        rememberLegs(selectedLegs);
    }, [selectedLegs, rememberLegs]);

    const buildFilterParams = useCallback(
        (page, size) => ({
            page,
            page_size: size,
            flight_number: filters.flightNumber || undefined,
            departure_airport: filters.departureAirport || undefined,
            arrival_airport: filters.arrivalAirport || undefined,
            start_date: filters.dateRange?.[0]?.format("YYYY-MM-DD"),
            end_date: filters.dateRange?.[1]?.format("YYYY-MM-DD"),
            aircraft_number: filters.aircraftNumber || undefined,
        }),
        [filters]
    );

    const fetchLegsPage = useCallback(
        async (page, size) => {
            const params = buildFilterParams(page, size);
            const response = useAuthoritativeBH
                ? await settlementApi.acmi.getRecords(params)
                : await settlementApi.costAllocation.getAllocationTargetLegs(params);

            if (!(response.data && response.data.data)) {
                throw new Error("API返回数据格式异常");
            }
            return response.data.data;
        },
        [buildFilterParams, useAuthoritativeBH]
    );

    /** 用 keys 还原完整航段并回传父组件（去重、可选 BH 校验） */
    const emitByKeys = useCallback(
        (keys) => {
            const uniqueKeys = [...new Set(keys)];
            const rows = [];
            const missing = [];

            uniqueKeys.forEach((id) => {
                const row = legsCacheRef.current.get(id);
                if (!row) {
                    missing.push(id);
                    return;
                }
                if (validateBH && !hasValidBH(row)) {
                    return;
                }
                rows.push(row);
            });

            if (missing.length > 0) {
                console.warn("选中航段缺少缓存数据，已跳过:", missing);
            }

            onSelectionChange(
                rows.map((r) => r.id),
                rows
            );
        },
        [validateBH, onSelectionChange]
    );

    const loadLegData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchLegsPage(currentPage, pageSize);
            const items = data.items || [];
            rememberLegs(items);
            setLegData(items);
            setTotalRecords(data.total || 0);
            console.log("获取到", items.length, "条航段数据");
        } catch (error) {
            console.error("获取航段数据失败:", error);
            message.error("获取航段数据失败: " + (error.message || "未知错误"));
            setLegData([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, fetchLegsPage, rememberLegs]);

    useEffect(() => {
        loadLegData();
    }, [loadLegData]);

    // 切换 BH / 平均分摊数据源时清空本地缓存（父组件也会清已选）
    useEffect(() => {
        legsCacheRef.current = new Map();
    }, [useAuthoritativeBH]);

    const selectableOnPage = useMemo(() => {
        if (!validateBH) return legData;
        return legData.filter(hasValidBH);
    }, [legData, validateBH]);

    const selectAllCurrentPage = useMemo(() => {
        if (selectableOnPage.length === 0) return false;
        const keySet = new Set(selectedRowKeys);
        return selectableOnPage.every((row) => keySet.has(row.id));
    }, [selectableOnPage, selectedRowKeys]);

    // 已选总 BH（与右上角预览同源算法，方便当场核对）
    const selectedTotalBH = useMemo(
        () => (selectedLegs || []).reduce((sum, leg) => sum + (parseFloat(leg.blockHours) || 0), 0),
        [selectedLegs]
    );

    const columns = [
        {
            title: "航班号",
            dataIndex: "flightNumber",
            key: "flightNumber",
            width: 100,
        },
        {
            title: "始发站",
            dataIndex: "departureAirport",
            key: "departureAirport",
            width: 80,
        },
        {
            title: "目的站",
            dataIndex: "arrivalAirport",
            key: "arrivalAirport",
            width: 80,
        },
        {
            title: "航班日期",
            dataIndex: "flightDate",
            key: "flightDate",
            width: 120,
        },
        {
            title: "飞机号",
            dataIndex: "aircraftNumber",
            key: "aircraftNumber",
            width: 100,
        },
        ...(showBHColumn
            ? [
                  {
                      title: "飞行小时 (BH)",
                      dataIndex: "blockHours",
                      key: "blockHours",
                      width: 120,
                      align: "right",
                      render: (val) => {
                          const valid = val != null && val > 0;
                          if (validateBH && showValidationStatus) {
                              return (
                                  <span
                                      style={{
                                          color: valid ? "#52c41a" : "#f5222d",
                                          fontWeight: "bold",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "flex-end",
                                      }}
                                  >
                                      {valid ? (
                                          <>
                                              <span>{val.toFixed(2)} h</span>
                                              <span style={{ marginLeft: 4, color: "#52c41a" }}>✓</span>
                                          </>
                                      ) : (
                                          <>
                                              <span>N/A</span>
                                              <span style={{ marginLeft: 4, color: "#f5222d" }}>✗</span>
                                          </>
                                      )}
                                  </span>
                              );
                          }
                          return (
                              <span style={{ color: valid ? "#1890ff" : "#bfbfbf", fontWeight: "bold" }}>
                                  {valid ? `${val.toFixed(2)} h` : "N/A"}
                              </span>
                          );
                      },
                  },
              ]
            : []),
    ];

    // 以 keys 为准；selectedRows 不可信（翻页后只含当前页）
    const rowSelection = {
        selectedRowKeys,
        preserveSelectedRowKeys: true,
        onChange: (keys) => {
            emitByKeys(keys);
        },
        getCheckboxProps: validateBH
            ? (record) => ({
                  disabled: !hasValidBH(record),
                  title: !hasValidBH(record) ? "该航段缺少有效BH数据，无法选择" : "",
              })
            : undefined,
    };

    const handleSelectAllCurrentPage = () => {
        const pageIds = new Set(selectableOnPage.map((row) => row.id));
        if (selectAllCurrentPage) {
            emitByKeys(selectedRowKeys.filter((id) => !pageIds.has(id)));
        } else {
            rememberLegs(selectableOnPage);
            emitByKeys([...new Set([...selectedRowKeys, ...selectableOnPage.map((row) => row.id)])]);
        }
    };

    /** 拉取当前筛选下全部航段并全选（分页累加，避免只选到一页） */
    const handleSelectAllFiltered = async () => {
        if (totalRecords <= 0) {
            message.warning("当前没有可选择的航段");
            return;
        }

        setSelectingAll(true);
        try {
            const allItems = [];
            const totalPages = Math.max(1, Math.ceil(totalRecords / MAX_FETCH_PAGE_SIZE));

            for (let page = 1; page <= totalPages; page += 1) {
                const data = await fetchLegsPage(page, MAX_FETCH_PAGE_SIZE);
                allItems.push(...(data.items || []));
            }

            rememberLegs(allItems);
            const selectable = validateBH ? allItems.filter(hasValidBH) : allItems;
            const keys = selectable.map((row) => row.id);

            emitByKeys(keys);
            message.success(
                `已全选筛选结果：${keys.length} 个航段` +
                    (validateBH ? `（已排除无有效 BH 的 ${allItems.length - keys.length} 条）` : "")
            );
        } catch (error) {
            console.error("全选全部筛选结果失败:", error);
            message.error("全选失败: " + (error.message || "未知错误"));
        } finally {
            setSelectingAll(false);
        }
    };

    const handleClearSelection = () => {
        emitByKeys([]);
    };

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const handleSearch = () => {
        setCurrentPage(1);
        // filters 已在 state；loadLegData 依赖 filters，下一轮 effect 会拉数
        // 若已在第 1 页，手动触发一次
        if (currentPage === 1) {
            loadLegData();
        }
    };

    const handleReset = () => {
        form.resetFields();
        setFilters({
            flightNumber: "",
            departureAirport: "",
            arrivalAirport: "",
            dateRange: null,
            aircraftNumber: "",
        });
        setCurrentPage(1);
    };

    const handlePageChange = (page, size) => {
        setCurrentPage(page);
        if (size !== pageSize) {
            setPageSize(size);
        }
    };

    const handlePageSizeChange = (_current, size) => {
        setCurrentPage(1);
        setPageSize(size);
    };

    return (
        <div>
            <Form form={form} layout="horizontal" className="filter-form" labelAlign="right">
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={4}>
                        <Form.Item name="flightNumber">
                            <Input
                                placeholder="航班号"
                                onChange={(e) => handleFilterChange("flightNumber", e.target.value)}
                                size="small"
                                allowClear
                            />
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="departureAirport">
                            <Input
                                placeholder="始发站"
                                onChange={(e) => handleFilterChange("departureAirport", e.target.value)}
                                size="small"
                                allowClear
                            />
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="arrivalAirport">
                            <Input
                                placeholder="目的站"
                                onChange={(e) => handleFilterChange("arrivalAirport", e.target.value)}
                                size="small"
                                allowClear
                            />
                        </Form.Item>
                    </Col>
                    <Col span={5}>
                        <Form.Item name="dateRange">
                            <RangePicker
                                placeholder={["开始日期", "结束日期"]}
                                onChange={(dates) => handleFilterChange("dateRange", dates)}
                                size="small"
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={3}>
                        <Form.Item name="aircraftNumber">
                            <Input
                                placeholder="飞机号"
                                onChange={(e) => handleFilterChange("aircraftNumber", e.target.value)}
                                size="small"
                                allowClear
                            />
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item>
                            <Space size="small">
                                <Button
                                    type="primary"
                                    icon={<SearchOutlined />}
                                    onClick={handleSearch}
                                    size="small"
                                    loading={loading}
                                >
                                    查询
                                </Button>
                                <Button icon={<ReloadOutlined />} onClick={handleReset} size="small">
                                    重置
                                </Button>
                            </Space>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            <div
                style={{
                    marginBottom: 16,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 8,
                }}
            >
                <Space wrap>
                    <Checkbox
                        checked={selectAllCurrentPage}
                        onChange={handleSelectAllCurrentPage}
                        disabled={selectableOnPage.length === 0 || loading || selectingAll}
                    >
                        全选当前页
                    </Checkbox>
                    <Button
                        size="small"
                        type="link"
                        onClick={handleSelectAllFiltered}
                        loading={selectingAll}
                        disabled={totalRecords === 0 || loading}
                    >
                        全选全部筛选结果（{totalRecords}）
                    </Button>
                    {selectedRowKeys.length > 0 && (
                        <Button size="small" type="link" danger onClick={handleClearSelection}>
                            清空已选
                        </Button>
                    )}
                    {(loading || selectingAll) && <Spin size="small" />}
                </Space>
                <span style={{ color: "#666", fontSize: 12 }}>
                    已选 <strong>{selectedRowKeys.length}</strong> 段
                    {showBHColumn && (
                        <>
                            ，合计 BH <strong>{selectedTotalBH.toFixed(2)}</strong> h
                        </>
                    )}
                </span>
            </div>

            <Table
                rowSelection={rowSelection}
                columns={columns}
                dataSource={legData}
                rowKey="id"
                size="small"
                bordered
                loading={loading || selectingAll}
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalRecords,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) =>
                        total === 0 ? "暂无符合条件的航段" : `第 ${range[0]}-${range[1]} 条 / 共 ${total} 条`,
                    pageSizeOptions: ["20", "50", "100", "200", "500"],
                    onShowSizeChange: handlePageSizeChange,
                    onChange: handlePageChange,
                }}
                scroll={{ x: 800 }}
                locale={{
                    emptyText: loading ? "加载中..." : "暂无符合条件的航段",
                }}
            />

            {selectedRowKeys.length > 0 && (
                <div
                    style={{
                        marginTop: 12,
                        padding: "8px 12px",
                        backgroundColor: "#e6f7ff",
                        border: "1px solid #91d5ff",
                        borderRadius: "6px",
                        fontSize: 12,
                    }}
                >
                    <span style={{ color: "#0958d9" }}>
                        已选择 {selectedRowKeys.length} 个航段（跨页累加），合计 BH {selectedTotalBH.toFixed(2)} h，将参与费用分摊计算
                    </span>
                </div>
            )}
        </div>
    );
};

export default TargetLegTable;
